import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../api/client';

export interface Family {
  id: string;
  name: string;
  description: string | null;
  avatar_url: string | null;
  member_count: number;
  my_role: 'owner' | 'admin' | 'member' | 'child' | 'guest';
}

export interface FamilyMember {
  id: string;
  user_id: string;
  username: string;
  first_name: string;
  last_name: string | null;
  avatar_url: string | null;
  role: 'owner' | 'admin' | 'member' | 'child' | 'guest';
  nickname: string | null;
  color: string | null;
  is_active: boolean;
  is_managed: boolean;
  joined_at: string;
  permissions: Record<string, boolean>;
}

export interface FamilyDetail extends Omit<Family, 'member_count'> {
  settings: Record<string, any>;
  created_at: string;
  updated_at: string;
  members: FamilyMember[];
}

interface FamilyState {
  families: Family[];
  currentFamily: FamilyDetail | null;
  isLoading: boolean;
  
  fetchFamilies: () => Promise<void>;
  fetchFamily: (id: string) => Promise<void>;
  createFamily: (name: string, description?: string) => Promise<Family>;
  setCurrentFamily: (family: FamilyDetail | null) => void;
  selectFamily: (id: string) => Promise<void>;
  autoSelectFamily: (lastFamilyId?: string | null) => Promise<void>;
  updateFamily: (id: string, data: Partial<FamilyDetail>) => Promise<void>;
  deleteFamily: (id: string) => Promise<void>;
  leaveFamily: (id: string) => Promise<void>;
}

export const useFamilyStore = create<FamilyState>()(
  persist(
    (set, get) => ({
      families: [],
      currentFamily: null,
      isLoading: false,

      fetchFamilies: async () => {
        set({ isLoading: true });
        try {
          const response = await api.get('/families/');
          set({ families: response.data, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      fetchFamily: async (id: string) => {
        set({ isLoading: true });
        try {
          const response = await api.get(`/families/${id}`);
          set({ currentFamily: response.data, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      createFamily: async (name: string, description?: string) => {
        const response = await api.post('/families/', { name, description });
        const newFamily = response.data;
        set((state) => ({
          families: [...state.families, {
            id: newFamily.id,
            name: newFamily.name,
            description: newFamily.description,
            avatar_url: newFamily.avatar_url,
            member_count: 1,
            my_role: 'owner'
          }]
        }));
        return newFamily;
      },

      setCurrentFamily: (family: FamilyDetail | null) => {
        set({ currentFamily: family });
      },

      selectFamily: async (id: string) => {
        // Select a family and save as last selected on the server
        set({ isLoading: true });
        try {
          const response = await api.get(`/families/${id}`);
          set({ currentFamily: response.data, isLoading: false });
          // Notify server of selection (fire and forget)
          api.post(`/families/${id}/select`).catch(() => {});
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      autoSelectFamily: async (lastFamilyId?: string | null) => {
        // Auto-select family based on:
        // 1. If user has only one family, select it automatically
        // 2. If user has multiple families, select the last selected one
        // 3. If no last selected, don't auto-select (let user choose)
        const { families, currentFamily, selectFamily, fetchFamilies } = get();
        
        // If already have a current family, don't change it
        if (currentFamily) return;
        
        // Fetch families if not loaded
        let familyList = families;
        if (familyList.length === 0) {
          await fetchFamilies();
          familyList = get().families;
        }
        
        if (familyList.length === 0) {
          // No families, nothing to select
          return;
        }
        
        if (familyList.length === 1) {
          // Only one family, auto-select it
          await selectFamily(familyList[0].id);
          return;
        }
        
        // Multiple families - try to use last selected
        if (lastFamilyId) {
          const lastFamily = familyList.find(f => f.id === lastFamilyId);
          if (lastFamily) {
            await selectFamily(lastFamily.id);
            return;
          }
        }
        
        // No last family or not found - don't auto-select, let user choose
      },

      updateFamily: async (id: string, data: Partial<FamilyDetail>) => {
        const response = await api.patch(`/families/${id}`, data);
        set((state) => ({
          currentFamily: state.currentFamily?.id === id ? response.data : state.currentFamily,
          families: state.families.map((f) =>
            f.id === id ? { ...f, ...data } : f
          )
        }));
      },

      deleteFamily: async (id: string) => {
        await api.delete(`/families/${id}`);
        set((state) => ({
          families: state.families.filter((f) => f.id !== id),
          currentFamily: state.currentFamily?.id === id ? null : state.currentFamily
        }));
      },

      leaveFamily: async (id: string) => {
        await api.post(`/families/${id}/leave`);
        set((state) => ({
          families: state.families.filter((f) => f.id !== id),
          currentFamily: state.currentFamily?.id === id ? null : state.currentFamily
        }));
      },
    }),
    {
      name: 'family-storage',
      partialize: (state) => ({ currentFamily: state.currentFamily }),
    }
  )
);

